import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface ThemeConfig {
  // Cores
  sidebarBg: string;
  sidebarAccent: string;
  pageBg: string;
  primaryColor: string;
  // Tipografia
  fontFamily: string;
  fontSize: 'sm' | 'md' | 'lg';
  fontWeight: 'light' | 'normal' | 'medium';
  // Imagens
  logoUrl: string;
  heroImageUrl: string;
  faviconUrl: string;
  /** Imagem de fundo da área de conteúdo central (base64 ou URL) */
  pageBgImage: string;
  /** Opacidade da imagem de fundo central (0–100) */
  pageBgImageOpacity: number;
  /** Modo de ajuste da imagem de fundo central */
  pageBgImageFit: 'cover' | 'contain' | 'repeat' | 'center';
  /** Posição vertical da imagem de fundo central */
  pageBgImagePosition: 'top' | 'center' | 'bottom';
  /** Efeito parallax (fixed) na imagem de fundo central */
  pageBgImageFixed: boolean;
  // Estilo geral
  borderRadius: 'sharp' | 'rounded' | 'pill';
  sidebarStyle: 'dark' | 'light' | 'colored';
  // Identidade SIADAP
  nomeCamara: string;
  subtituloSidebar: string;
  pageTitle: string;
  // Navegação SIADAP
  navBoldItems: string[];
  navIcons: Record<string, string>;
  navTextColor: 'auto' | 'black' | 'white';
  // Tamanhos de imagens
  logoSize: number;       // altura do logo em px (20–80)
  heroImageSize: number;  // opacidade/escala da imagem de fundo (10–100%)

  // ── Aparência do módulo Gestão da Formação ──
  formacaoNome: string;
  formacaoSubtitulo: string;
  formacaoAccentColor: string;
  formacaoSidebarBg: string;
  formacaoSidebarStyle: 'dark' | 'light' | 'colored';
  formacaoNavIcons: Record<string, string>;
  formacaoNavBoldItems: string[];
  formacaoNavTextColor: 'auto' | 'black' | 'white';
  formacaoLogoUrl: string;
  formacaoHeroImageUrl: string;
  formacaoLogoSize: number;
  formacaoHeroImageSize: number;
}

const DEFAULT_THEME: ThemeConfig = {
  sidebarBg: '#0a1f18',
  sidebarAccent: '#1a7a4e',
  pageBg: '#f7f7f5',
  primaryColor: '#0f392b',
  fontFamily: 'Inter',
  fontSize: 'md',
  fontWeight: 'normal',
  logoUrl: '',
  heroImageUrl: '',
  faviconUrl: '',
  pageBgImage: '',
  pageBgImageOpacity: 15,
  pageBgImageFit: 'cover',
  pageBgImagePosition: 'center',
  pageBgImageFixed: false,
  borderRadius: 'rounded',
  sidebarStyle: 'dark',
  nomeCamara: 'Câmara Municipal',
  subtituloSidebar: 'SIADAP para RH',
  pageTitle: '',
  navBoldItems: ['/progressoes'],
  navIcons: {},
  navTextColor: 'auto',
  logoSize: 26,
  heroImageSize: 100,
  // Gestão da Formação defaults
  formacaoNome: 'Gabinete de Formação',
  formacaoSubtitulo: 'Gestão da Formação',
  formacaoAccentColor: '#16a34a',
  formacaoSidebarBg: '#ffffff',
  formacaoSidebarStyle: 'light',
  formacaoNavIcons: {},
  formacaoNavBoldItems: ['/formacao'],
  formacaoNavTextColor: 'auto',
  formacaoLogoUrl: '',
  formacaoHeroImageUrl: '',
  formacaoLogoSize: 26,
  formacaoHeroImageSize: 100,
};

interface ThemeContextType {
  theme: ThemeConfig;
  setTheme: (t: Partial<ThemeConfig>) => void;
  saveTheme: () => Promise<void>;
  reloadTheme: () => Promise<void>;
  saving: boolean;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  saveTheme: async () => {},
  reloadTheme: async () => {},
  saving: false,
});

const THEME_KEY = 'app_theme';

function applyThemeToDOM(theme: ThemeConfig) {
  const root = document.documentElement;
  root.style.setProperty('--theme-sidebar-bg', theme.sidebarBg);
  root.style.setProperty('--theme-sidebar-accent', theme.sidebarAccent);
  root.style.setProperty('--theme-page-bg', theme.pageBg);
  root.style.setProperty('--theme-primary', theme.primaryColor);

  const fontSizeMap = { sm: '13px', md: '14px', lg: '15px' };
  const fontWeightMap = { light: '300', normal: '400', medium: '500' };
  root.style.setProperty('--theme-font-size', fontSizeMap[theme.fontSize]);
  root.style.setProperty('--theme-font-weight', fontWeightMap[theme.fontWeight]);

  const radiusMap = { sharp: '4px', rounded: '8px', pill: '16px' };
  root.style.setProperty('--theme-radius', radiusMap[theme.borderRadius]);

  // Font family
  const fontMap: Record<string, string> = {
    Inter: "'Inter', system-ui, sans-serif",
    'DM Sans': "'DM Sans', system-ui, sans-serif",
    'Nunito': "'Nunito', system-ui, sans-serif",
    'Outfit': "'Outfit', system-ui, sans-serif",
    'Plus Jakarta Sans': "'Plus Jakarta Sans', system-ui, sans-serif",
    'Geist': "'Geist', system-ui, sans-serif",
  };
  root.style.setProperty('--theme-font-family', fontMap[theme.fontFamily] ?? fontMap['Inter']);

  // Body background
  document.body.style.backgroundColor = theme.pageBg;
  document.body.style.fontFamily = fontMap[theme.fontFamily] ?? fontMap['Inter'];

  // Favicon dinâmico
  if (theme.faviconUrl) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = theme.faviconUrl;
  }

  // Título da página
  if (theme.pageTitle) {
    document.title = theme.pageTitle;
  }
}

// Tentar carregar tema do localStorage para evitar flash de cor inicial
function getInitialTheme(): ThemeConfig {
  try {
    const cached = localStorage.getItem('app_theme_cache');
    if (cached) {
      const parsed = JSON.parse(cached) as Partial<ThemeConfig>;
      return { ...DEFAULT_THEME, ...parsed };
    }
  } catch { /* ignora */ }
  return DEFAULT_THEME;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeConfig>(() => {
    const initial = getInitialTheme();
    applyThemeToDOM(initial);
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const loadTheme = useCallback(async () => {
    try {
      // Obter municipio_id do utilizador atual
      const { data: { session } } = await supabase.auth.getSession();
      let municipioId = '00000000-0000-0000-0000-000000000001';
      if (session) {
        const { data: profileData } = await supabase
          .from('profiles')
          .select('municipio_id')
          .eq('id', session.user.id)
          .maybeSingle();
        if (profileData?.municipio_id) {
          municipioId = profileData.municipio_id;
        }
      }

      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', THEME_KEY)
        .eq('municipio_id', municipioId)
        .maybeSingle();
      if (data?.value) {
        const parsed = JSON.parse(data.value) as Partial<ThemeConfig>;
        const merged: ThemeConfig = { ...DEFAULT_THEME, ...parsed };
        // Guardar em cache para evitar flash na próxima visita
        try { localStorage.setItem('app_theme_cache', JSON.stringify(merged)); } catch { /* ignora */ }
        setThemeState(merged);
        applyThemeToDOM(merged);
      } else {
        try { localStorage.removeItem('app_theme_cache'); } catch { /* ignora */ }
        applyThemeToDOM(DEFAULT_THEME);
      }
    } catch {
      applyThemeToDOM(DEFAULT_THEME);
    }
  }, []);

  useEffect(() => {
    // Recarregar tema quando o utilizador muda
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')) {
        loadTheme();
      } else if (!session) {
        setThemeState(DEFAULT_THEME);
        applyThemeToDOM(DEFAULT_THEME);
      }
    });
    return () => subscription.unsubscribe();
  }, [loadTheme]);

  const setTheme = useCallback((partial: Partial<ThemeConfig>) => {
    setThemeState((prev) => {
      const next = { ...prev, ...partial };
      applyThemeToDOM(next);
      return next;
    });
  }, []);

  const saveTheme = useCallback(async () => {
    setSaving(true);
    try {
      // Usar a edge function para garantir permissões via service_role
      const { error } = await supabase.functions.invoke('save-settings', {
        body: {
          settings: {
            [THEME_KEY]: JSON.stringify(theme),
          },
        },
      });
      if (error) {
        console.error('Erro ao guardar tema:', error);
      }
    } finally {
      setSaving(false);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, saveTheme, reloadTheme: loadTheme, saving }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
